package com.videoanalyzer.web.components.auth

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.components.Logo
import com.videoanalyzer.web.components.LogoSize
import com.videoanalyzer.web.components.LogoText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Landing page highlighting value proposition.
 */
@Composable
fun LandingPage(
    onSignIn: () -> Unit,
    onSignUp: () -> Unit,
    onTermsOfService: () -> Unit,
    onPrivacyPolicy: () -> Unit,
    onContact: () -> Unit
) {
    Div({
        style {
            minHeight(100.vh)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        // Navigation bar - Glassmorphism
        Nav({
            classes("nav-glass")
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                padding(16.px, 32.px)
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                right(0.px)
                property("z-index", "100")
            }
        }) {
            // Logo
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                Logo(size = LogoSize.MEDIUM)
                LogoText(size = LogoSize.MEDIUM)
            }

            // Auth buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                Button({
                    classes("btn")
                    style {
                        backgroundColor(Color.transparent)
                        color(Color("#FAFAFA"))
                        padding(10.px, 20.px)
                        fontWeight(500)
                    }
                    onClick { onSignIn() }
                }) { Text("Sign In") }

                Button({
                    classes("btn", "btn-primary")
                    style {
                        padding(10.px, 24.px)
                        fontWeight(600)
                    }
                    onClick { onSignUp() }
                }) { Text("Get Started Free") }
            }
        }

        // Hero Section
        Section({
            style {
                paddingTop(140.px)
                paddingBottom(80.px)
                paddingLeft(24.px)
                paddingRight(24.px)
                textAlign("center")
                position(Position.Relative)
                overflow("hidden")
            }
        }) {
            // Floating background elements
            FloatingScoreElements()

            Div({
                style {
                    maxWidth(900.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                    position(Position.Relative)
                    property("z-index", "1")
                }
            }) {
                // Badge - neutral white/gray
                Div({
                    classes("fade-in-up-hero")
                    style {
                        display(DisplayStyle.InlineBlock)
                        padding(8.px, 16.px)
                        borderRadius(50.px)
                        backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                        property("border", "1px solid rgba(255, 255, 255, 0.1)")
                        marginBottom(24.px)
                        property("backdrop-filter", "blur(8px)")
                    }
                }) {
                    Span({
                        style {
                            color(Color("#FAFAFA"))
                            fontSize(14.px)
                            fontWeight(500)
                            letterSpacing(0.5.px)
                        }
                    }) { Text("Powered by Advanced AI") }
                }

                // Main headline
                H1({
                    classes("fade-in-up-hero", "hero-title", "stagger-1")
                    style {
                        color(Color("#FAFAFA"))
                        marginBottom(24.px)
                    }
                }) {
                    Text("Get More Views on ")
                    Span({
                        classes("text-youtube")
                    }) { Text("YouTube") }
                }

                // Subheadline
                P({
                    classes("fade-in-up-hero", "hero-subtitle", "stagger-2")
                    style {
                        margin(0.px, "auto".unsafeCast<CSSNumeric>(), 40.px, "auto".unsafeCast<CSSNumeric>())
                    }
                }) {
                    Text("AI analyzes your titles, thumbnails, and content strategy to give you actionable insights that actually grow your channel.")
                }

                // CTA Buttons
                Div({
                    classes("fade-in-up-hero", "stagger-3")
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        gap(16.px)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    Button({
                        classes("btn", "btn-primary", "btn-large")
                        style {
                            padding(18.px, 36.px)
                            fontSize(17.px)
                            fontWeight(600)
                            borderRadius(12.px)
                        }
                        onClick { onSignUp() }
                    }) {
                        Text("Start Free Analysis")
                        Span({ style { marginLeft(8.px) } }) { Text("→") }
                    }

                    Button({
                        classes("btn", "btn-secondary", "btn-large")
                        style {
                            padding(18.px, 36.px)
                            fontSize(17.px)
                            borderRadius(12.px)
                        }
                        onClick { onSignIn() }
                    }) { Text("Sign In") }
                }

                // Trust indicators
                Div({
                    classes("fade-in-up-hero", "stagger-4")
                    style {
                        marginTop(48.px)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        gap(32.px)
                        flexWrap(FlexWrap.Wrap)
                        color(Color("#71717A"))
                        fontSize(14.px)
                    }
                }) {
                    TrustItem("✓", "No credit card required")
                    TrustItem("✓", "3 free analyses per month")
                    TrustItem("✓", "Instant results")
                }
            }
        }

        // Features Section
        Section({
            style {
                padding(80.px, 24.px)
            }
        }) {
            Div({
                style {
                    maxWidth(1200.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
                // Section header
                Div({
                    classes("fade-in-up")
                    style {
                        textAlign("center")
                        marginBottom(64.px)
                    }
                }) {
                    H2({
                        classes("section-title")
                        style {
                            color(Color("#FAFAFA"))
                            marginBottom(16.px)
                        }
                    }) { Text("Everything You Need to Grow") }
                    P({
                        style {
                            fontSize(18.px)
                            color(Color("#A1A1AA"))
                            maxWidth(600.px)
                            margin(0.px, "auto".unsafeCast<CSSNumeric>())
                            lineHeight("1.6")
                        }
                    }) { Text("Comprehensive analysis across 13 key factors that determine your video's success") }
                }

                // Features grid
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 350px), 1fr))")
                        gap(24.px)
                    }
                }) {
                    Div({ classes("fade-in-up", "stagger-1") }) {
                        FeatureCard(
                            icon = "📝",
                            title = "Title Optimization",
                            description = "Get a score on curiosity gap, clarity, emotional triggers, keywords, and length. Plus AI-generated title suggestions.",
                            color = "#FF0000"
                        )
                    }
                    Div({ classes("fade-in-up", "stagger-2") }) {
                        FeatureCard(
                            icon = "🖼️",
                            title = "Thumbnail Analysis",
                            description = "Analyze faces, text, colors, and composition. Know exactly what's working and what needs improvement.",
                            color = "#FF0000"
                        )
                    }
                    Div({ classes("fade-in-up", "stagger-3") }) {
                        FeatureCard(
                            icon = "📊",
                            title = "Performance Insights",
                            description = "Understand your engagement rates, viral potential, and how your video compares to similar content.",
                            color = "#FF0000"
                        )
                    }
                    Div({ classes("fade-in-up", "stagger-4") }) {
                        FeatureCard(
                            icon = "🔍",
                            title = "SEO & Tags",
                            description = "Discover missing keywords and get suggested tags to improve your search rankings and discoverability.",
                            color = "#FF0000"
                        )
                    }
                    Div({ classes("fade-in-up", "stagger-5") }) {
                        FeatureCard(
                            icon = "⏱️",
                            title = "Retention Strategy",
                            description = "Get hook suggestions, pacing tips, and chapter recommendations to keep viewers watching longer.",
                            color = "#FF0000"
                        )
                    }
                    Div({ classes("fade-in-up", "stagger-6") }) {
                        FeatureCard(
                            icon = "🚀",
                            title = "Growth Recommendations",
                            description = "Actionable quick wins prioritized by impact. Know exactly what to fix first for maximum results.",
                            color = "#FF0000"
                        )
                    }
                }
            }
        }

        // How It Works Section
        Section({
            style {
                padding(80.px, 24.px)
                backgroundColor(Color("rgba(0, 0, 0, 0.3)"))
            }
        }) {
            Div({
                style {
                    maxWidth(1000.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                    textAlign("center")
                }
            }) {
                H2({
                    classes("section-title", "fade-in-up")
                    style {
                        color(Color("#FAFAFA"))
                        marginBottom(64.px)
                    }
                }) { Text("How It Works") }

                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fit, minmax(280px, 1fr))")
                        gap(40.px)
                    }
                }) {
                    StepCard("1", "Paste Your Video URL", "Just paste any YouTube video URL and click analyze.")
                    StepCard("2", "AI Analyzes Everything", "Our AI examines 13+ factors including title, thumbnail, SEO, and engagement.")
                    StepCard("3", "Get Actionable Insights", "Receive prioritized recommendations sorted by impact and effort required.")
                }
            }
        }

        // Stats Section
        Section({
            style {
                padding(80.px, 24.px)
            }
        }) {
            Div({
                classes("card")
                style {
                    maxWidth(1000.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                    padding(48.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                        gap(32.px)
                        textAlign("center")
                    }
                }) {
                    StatItem("10,000+", "Videos Analyzed")
                    StatItem("35%", "Avg. CTR Improvement")
                    StatItem("5,000+", "Happy Creators")
                    StatItem("13", "Analysis Factors")
                }
            }
        }

        // Final CTA Section
        Section({
            style {
                padding(100.px, 24.px)
                textAlign("center")
            }
        }) {
            Div({
                classes("fade-in-up")
                style {
                    maxWidth(600.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
                H2({
                    classes("section-title")
                    style {
                        color(Color("#FAFAFA"))
                        marginBottom(16.px)
                    }
                }) { Text("Ready to Grow Your Channel?") }

                P({
                    style {
                        fontSize(18.px)
                        color(Color("#A1A1AA"))
                        marginBottom(40.px)
                        lineHeight("1.6")
                    }
                }) { Text("Join thousands of creators using AI to optimize their content.") }

                Button({
                    classes("btn", "btn-primary", "btn-large")
                    style {
                        padding(20.px, 48.px)
                        fontSize(18.px)
                        fontWeight(600)
                        borderRadius(12.px)
                    }
                    onClick { onSignUp() }
                }) {
                    Text("Get Started Free")
                    Span({ style { marginLeft(8.px) } }) { Text("→") }
                }
            }
        }

        // Footer
        Footer({
            style {
                property("border-top", "1px solid #27272A")
                padding(32.px, 24.px)
                textAlign("center")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(10.px)
                    marginBottom(16.px)
                }
            }) {
                Logo(size = LogoSize.SMALL)
                LogoText(size = LogoSize.SMALL)
            }

            // Footer links
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(24.px)
                    marginBottom(16.px)
                }
            }) {
                FooterLink("Terms of Service", onTermsOfService)
                FooterLink("Privacy Policy", onPrivacyPolicy)
                FooterLink("Contact Us", onContact)
            }

            P({
                style {
                    color(Color("#475569"))
                    fontSize(12.px)
                }
            }) {
                val currentYear = kotlin.js.Date().getFullYear().toInt()
                Text("© $currentYear VideoScore. All rights reserved.")
            }
        }
    }
}

@Composable
private fun FloatingScoreElements() {
    // Floating score indicators in background
    Div({
        classes("floating-element")
        style {
            top(15.percent)
            left(10.percent)
            fontSize(48.px)
            property("animation-delay", "0s")
        }
    }) { Text("87") }

    Div({
        classes("floating-element")
        style {
            top(25.percent)
            right(12.percent)
            fontSize(56.px)
            property("animation-delay", "-2s")
        }
    }) { Text("92") }

    Div({
        classes("floating-element")
        style {
            bottom(30.percent)
            left(15.percent)
            fontSize(40.px)
            property("animation-delay", "-4s")
        }
    }) { Text("76") }
}

@Composable
private fun TrustItem(icon: String, text: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
        }
    }) {
        Span({ style { color(Color("#22C55E")) } }) { Text(icon) }
        Span { Text(text) }
    }
}

@Composable
private fun FeatureCard(icon: String, title: String, description: String, color: String) {
    Div({
        classes("feature-card")
        style {
            padding(32.px)
            property("border-top-color", color)
        }
    }) {
        Div({
            style {
                width(56.px)
                height(56.px)
                borderRadius(12.px)
                backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                marginBottom(20.px)
                fontSize(28.px)
            }
        }) { Text(icon) }

        H3({
            style {
                fontFamily("'Sora', sans-serif")
                fontSize(20.px)
                fontWeight(600)
                color(Color("#FAFAFA"))
                marginBottom(12.px)
            }
        }) { Text(title) }

        P({
            style {
                fontSize(15.px)
                color(Color("#A1A1AA"))
                lineHeight("1.7")
                margin(0.px)
            }
        }) { Text(description) }
    }
}

@Composable
private fun StepCard(number: String, title: String, description: String) {
    Div({
        style {
            textAlign("center")
        }
    }) {
        Div({
            style {
                width(64.px)
                height(64.px)
                borderRadius(16.px)
                backgroundColor(Color("#E53935"))
                property("box-shadow", "0 4px 20px rgba(255, 0, 0, 0.3)")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                margin(0.px, "auto".unsafeCast<CSSNumeric>(), 24.px, "auto".unsafeCast<CSSNumeric>())
                fontFamily("'Sora', sans-serif")
                fontSize(28.px)
                fontWeight(700)
                color(Color.white)
            }
        }) { Text(number) }

        H3({
            style {
                fontFamily("'Sora', sans-serif")
                fontSize(20.px)
                fontWeight(600)
                color(Color("#FAFAFA"))
                marginBottom(12.px)
            }
        }) { Text(title) }

        P({
            style {
                fontSize(15.px)
                color(Color("#A1A1AA"))
                lineHeight("1.7")
            }
        }) { Text(description) }
    }
}

@Composable
private fun StatItem(value: String, label: String) {
    Div {
        Div({
            classes("text-glow-cyan")
            style {
                fontSize(42.px)
                fontWeight(800)
                color(Color("#0EA5E9"))
                fontFamily("'SF Mono', Monaco, monospace")
            }
        }) { Text(value) }
        Div({
            style {
                fontSize(15.px)
                color(Color("#A1A1AA"))
                marginTop(8.px)
            }
        }) { Text(label) }
    }
}
